import React from "react";
import { formatMerchantCurrency } from "../data/merchantData";

function toMoneyLabel(value) {
  return formatMerchantCurrency(value, 2);
}

export default function MerchantLineChart({ data }) {
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div className="merchant-chart-empty">
        No sales trend data yet.
      </div>
    );
  }

  if (data.length === 1) {
    return (
      <div className="merchant-chart-empty">
        Sales trend needs more than one data point to render.
      </div>
    );
  }

  const values = data.map(function mapPoint(point) {
    return point.value;
  });
  const width = 680;
  const height = 152;
  const padding = { left: 0, right: 12, top: 12, bottom: 12 };
  const minValue = Math.min.apply(null, values);
  const maxValue = Math.max.apply(null, values);
  const valueSpan = maxValue - minValue || 1;

  function getX(index) {
    return (
      padding.left +
      (index * (width - padding.left - padding.right)) / (data.length - 1)
    );
  }

  function getY(value) {
    return (
      padding.top +
      (height - padding.top - padding.bottom) *
        (1 - (value - minValue) / valueSpan)
    );
  }

  let linePath = "M" + getX(0) + "," + getY(values[0]);
  let areaPath = linePath;

  for (let index = 1; index < values.length; index += 1) {
    const controlX = (getX(index - 1) + getX(index)) / 2;
    const segment =
      " C" +
      controlX +
      "," +
      getY(values[index - 1]) +
      " " +
      controlX +
      "," +
      getY(values[index]) +
      " " +
      getX(index) +
      "," +
      getY(values[index]);

    linePath += segment;
    areaPath += segment;
  }

  areaPath +=
    " L" +
    getX(values.length - 1) +
    "," +
    (height - padding.bottom) +
    " L" +
    getX(0) +
    "," +
    (height - padding.bottom) +
    " Z";

  const ticks = [minValue, (minValue + maxValue) / 2, maxValue];
  const visibleLabelIndexes = data.reduce(function collectLabelIndexes(result, point, index) {
    if (index % 5 === 0) {
      result.push(index);
    }

    return result;
  }, []);

  if (visibleLabelIndexes[visibleLabelIndexes.length - 1] !== data.length - 1) {
    visibleLabelIndexes.push(data.length - 1);
  }

  return (
    <div className="merchant-line-chart">
      <div className="merchant-line-chart-body">
        <div className="merchant-line-chart-y-axis">
          {ticks.map(function renderTickLabel(tick, index) {
            return (
              <span
                key={index}
                className="merchant-line-chart-y-label"
                style={{ top: getY(tick) }}
              >
                {toMoneyLabel(tick)}
              </span>
            );
          })}
        </div>

        <div className="merchant-line-chart-plot">
          <svg
            className="merchant-chart"
            viewBox={"0 0 " + width + " " + height}
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="merchantChartGradient" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="rgba(30,58,138,0.15)" />
                <stop offset="100%" stopColor="rgba(30,58,138,0)" />
              </linearGradient>
            </defs>
            {ticks.map(function renderTick(tick, index) {
              return (
                <line
                  key={index}
                  x1={padding.left}
                  y1={getY(tick)}
                  x2={width - padding.right}
                  y2={getY(tick)}
                  stroke="var(--merchant-border)"
                  strokeWidth="1"
                  strokeDasharray="4 3"
                />
              );
            })}
            <path d={areaPath} fill="url(#merchantChartGradient)" />
            <path
              d={linePath}
              fill="none"
              stroke="#1e3a8a"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          <div className="merchant-line-chart-x-axis">
            {visibleLabelIndexes.map(function renderLabel(index) {
              const point = data[index];
              const leftPosition =
                ((getX(index) - padding.left) / (width - padding.left - padding.right)) * 100;

              return (
                <span
                  key={point.label + "-" + index}
                  className="merchant-line-chart-x-label"
                  style={{ left: leftPosition + "%" }}
                >
                  {point.label}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
